import { useTranslation } from "@/i18n"
import { PostTypeFilter } from "@/components/PostTypeFilter"
import { MEDIA_FILTERS, type MediaFilter } from "@/nostr/api/filters/mediaFilter"
import { LANGUAGE_OPTIONS } from "@/nostr/api/filters/languageFilter"
import { postTypeSelection } from "@/nostr/api/filters/filterEngine"
import { FilterChipGroup, FilterField, FilterSection, type FilterCategoryProps } from "./FilterSection"
import { TopicsFilter } from "./TopicsFilter"

const MEDIA_LABELS: Record<MediaFilter, string> = {
  text: "Text only",
  image: "Images",
  video: "Videos",
  audio: "Audio",
  link: "Links",
  media: "With media",
}

const MEDIA_OPTIONS = MEDIA_FILTERS.map((value) => ({ value, label: MEDIA_LABELS[value] }))

export interface ContentFiltersProps extends FilterCategoryProps {
  /** Number of muted words configured in Settings. */
  mutedWordCount?: number
  /** Pages whose tabs already select the post type hide this control. */
  hidePostType?: boolean
}

/**
 * CONTENT: post type, media type, language and muted words.
 *
 * Post type reuses the shared `PostTypeFilter` control, and media / language use
 * the classification rules of `src/nostr/api/filters` — the same functions the
 * engine applies to the list.
 */
export function ContentFilters({
  value,
  onChange,
  onReset,
  mutedWordCount = 0,
  hidePostType = false,
}: ContentFiltersProps) {
  const { t } = useTranslation()
  const selection = postTypeSelection(value)

  const toggleMedia = (media: MediaFilter) => {
    const next = value.media.includes(media)
      ? value.media.filter((item) => item !== media)
      : [...value.media, media]
    onChange({ media: next })
  }

  return (
    <FilterSection id="filter-section-content" title={t("Content")} onReset={onReset}>
      {!hidePostType && (
        <FilterField label={t("Post type")}>
          <PostTypeFilter
            value={selection}
            onChange={(next) => onChange({ postTypes: next === "all" ? [] : [next] })}
          />
        </FilterField>
      )}

      <FilterField
        label={t("Media type")}
        hint={t("Posts with several media types match every selected type.")}
      >
        <FilterChipGroup
          options={MEDIA_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) }))}
          selected={value.media}
          onToggle={toggleMedia}
          ariaLabel={t("Media type")}
        />
      </FilterField>

      <TopicsFilter topics={value.topics} mode={value.topicMode} onChange={onChange} />

      <FilterField
        label={t("Language")}
        hint={t("Posts with no detectable language are kept when a language is selected.")}
      >
        <select
          value={value.language}
          onChange={(event) => onChange({ language: event.target.value })}
          aria-label={t("Language")}
        >
          <option value="all">{t("All languages")}</option>
          <option value="detected">{t("Detected language only")}</option>
          {LANGUAGE_OPTIONS.map((option) => (
            <option key={option.code} value={option.code}>
              {t(option.label)}
            </option>
          ))}
        </select>
      </FilterField>

      <label className="filter-toggle">
        <input
          type="checkbox"
          checked={value.hideMutedWords}
          onChange={(event) => onChange({ hideMutedWords: event.target.checked })}
        />
        <span>
          {t("Hide posts containing muted words")}
          {mutedWordCount > 0 && (
            <span className="muted small"> · {t("{n} words configured", { n: mutedWordCount })}</span>
          )}
        </span>
      </label>
    </FilterSection>
  )
}
